import { Button, Checkbox } from '@nextui-org/react'
import { FaListOl } from '@react-icons/all-files/fa6/FaListOl'
import { VscListOrdered } from '@react-icons/all-files/vsc/VscListOrdered'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCodePlan, useConversationUuid, useTaskStatus } from '~/hooks'
import { useTaskId } from '~/hooks/task/useTaskId'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { I18nKey } from '~/i18n/declaration'
import { AgentTaskStatus, CodePlan, DiffType, diffTypeToJSON } from '~/protos/agents'
import { approvePlan } from '~/services/taskService'
import { BetterToast } from '~/utils/toast'
import { cm } from '~/utils/utils'
import { DocMarkdown } from '../documents'
import { SVGPlan } from './SVGPlan'

export interface CodePlanViewerProps {
  codePlan?: CodePlan
  readOnly?: boolean
}

export function CodePlanViewer({ codePlan, readOnly = false }: CodePlanViewerProps): JSX.Element {
  const uuid = useConversationUuid()
  const taskId = useTaskId()
  const { currentWorkspace } = useWorkspaceContext()
  const [isSubmitPlanDisabled, setIsSubmitPlanDisabled] = useState(false)
  const { t } = useTranslation()
  const [selectedFileNames, setSelectedFileNames] = useState(
    codePlan?.fileMultiSelect?.selectedFileNames || []
  )
  const taskStatus = useTaskStatus()
  const isReadOnly = AgentTaskStatus.PLANNING_COMPLETED !== taskStatus || readOnly

  const { svgFlows = [] } = codePlan || {}

  const onSubmitPlan = async () => {
    if (!taskId || !currentWorkspace?.uuid) return
    setIsSubmitPlanDisabled(true)
    const editedPlan = JSON.parse(JSON.stringify(codePlan)) as CodePlan
    editedPlan!.fileMultiSelect!.selectedFileNames = selectedFileNames
    await approvePlan({ workspaceUuid: currentWorkspace.uuid, uuid, taskId, codePlan: editedPlan })
    BetterToast.success('Plan submitted')
  }

  useEffect(() => {
    if (isSubmitPlanDisabled) {
      setIsSubmitPlanDisabled(false)
    }
  }, [uuid, isSubmitPlanDisabled])

  useEffect(() => {
    const selectedFileNames = codePlan?.fileMultiSelect?.selectedFileNames
    if (Array.isArray(selectedFileNames)) {
      setSelectedFileNames(selectedFileNames)
    }
  }, [codePlan])

  const renderPlan = () => {
    const changeTypeColors = {
      [DiffType.MODIFY]: 'text-[#eb5325]',
      [DiffType.ADD]: 'text-[#2e7d32]',
      [DiffType.DELETE]: 'text-[#d32f2f]',
      [DiffType.DIFF_UNKNOWN_TYPE]: 'text-gray-700'
    }
    const { selectedFileNames } = codePlan!.fileMultiSelect!
    const allOptions = codePlan!.fileMultiSelect!.options
    const options = isReadOnly
      ? allOptions.filter(option => selectedFileNames.includes(option.fileName))
      : allOptions

    return options.map(option => {
      const changeTypeClass =
        changeTypeColors[option.fileChangeType as keyof typeof changeTypeColors]
      if (svgFlows.length) {
        return (
          <SVGPlan
            key={option.fileName}
            codePlan={codePlan}
            svgFlows={svgFlows}
            hideSubmitButton={isReadOnly}
          />
        )
      }

      return (
        <div key={option.fileName} className='pr-[30px]'>
          <div className='plan my-[30px]' key={option.fileName}>
            <div className='plan-title ml-[40px] mb-2 flex pb-[10px] border-b items-center'>
              {isReadOnly ? null : (
                <Checkbox
                  defaultSelected={selectedFileNames.includes(option.fileName)}
                  size='md'
                  radius='sm'
                  onChange={evnet => {
                    setSelectedFileNames(prev => {
                      if (evnet.target.checked) {
                        return [...prev, option.fileName]
                      } else {
                        return prev.filter(name => name !== option.fileName)
                      }
                    })
                  }}
                />
              )}
              <div
                className={cm(
                  'plan-label rounded-md text-white font-semibold text-xs',
                  'whitespace-nowrap flex items-center pt-[2.5px]',
                  changeTypeClass
                )}
              >
                {diffTypeToJSON(option.fileChangeType)}
              </div>
              <div
                className={cm(
                  'plan-changes ml-2 text-default-600',
                  'whitespace-nowrap text-ellipsis overflow-hidden'
                )}
              >
                {option.fileName}
              </div>
            </div>
            <DocMarkdown text={option.plan} />
          </div>
        </div>
      )
    })
  }

  return codePlan ? (
    <div className='w-full h-full bg-white'>
      {renderPlan()}
      {isReadOnly || svgFlows.length > 0 ? null : (
        <div className='flex items-center justify-end pr-7' key='submit-button'>
          <Button
            isDisabled={!taskId || isSubmitPlanDisabled}
            disableRipple={true}
            onClick={onSubmitPlan}
            className={cm(
              'button-submit bg-light-success text-white',
              'py-3 px-4 rounded-xl cursor-pointer',
              'border border-[#1c875e] select-none h-12 hover:bg-[#20986a]',
              'disabled:cursor-not-allowed '
            )}
          >
            Approve
          </Button>
        </div>
      )}
    </div>
  ) : (
    <div className='w-full h-full flex flex-col text-neutral-400 items-center justify-center bg-white'>
      <VscListOrdered size={100} />
      {t(I18nKey.PLANNER$EMPTY_MESSAGE)}
    </div>
  )
}

interface PlannerProps {
  readOnly: boolean
}

const Planner = ({ readOnly }: PlannerProps): JSX.Element => {
  const codePlan = useCodePlan()
  return (
    <div className='w-[496px] h-auto border mt-8 -translate-x-10 z-50'>
      <div className='bg-white'>
        <div className='h-12 w-full bg-light-primary px-4 py-3 flex items-center'>
          <FaListOl />
          <div className='ml-2 body-md-bold'>Plan</div>
        </div>
        <div className='p-4 pt-1'>
          <div className='w-full'>
            <CodePlanViewer codePlan={codePlan} readOnly={readOnly} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Planner
